import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
  RadioGroup,
  Radio,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {useEffect, useMemo, useState} from 'react';
import {
  intercomDeviceServiceCreate,
  intercomDeviceServiceUpdate,
  apartmentServiceFind,
  amenityServiceFind,
  getAvailableModules,
  getDeviceModules,
  createDeviceModules,
  getRoomConfig,
  updateRoomConfig,
} from 'src/apis/autoGenerate/services';
import {
  IntercomDevice,
  AvailableModule,
  DeviceModule,
  IntercomDeviceTypeEnum,
  DeviceModuleTypeEnum,
  DeviceModuleDeviceTypeEnum,
  RoomSortByEnum,
  RoomDisplayFormatEnum,
} from 'src/apis/autoGenerate/types/intercomDevice';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {Amenity} from 'src/apis/autoGenerate/types/amenity';
import message from 'src/components/Message';
import Modal from 'src/components/Modal';
import {AutocompleteVal} from 'src/components/SearchApartment';
import {useApartmentBuildingUnit, useOrgId} from 'src/hooks';
import MacAddressInput from 'src/components/MacAddressInput';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import LogoUpload from 'src/components/LogoUpload';
import {TIMEZONE_OPTIONS} from 'src/constants/timezones';

interface AddDeviceModalProps {
  open: boolean;
  handleConfirm: VoidFunction;
  handleCancel: VoidFunction;
  initData?: IntercomDevice;
}

interface SelectedModule {
  id?: string; // Add id field to track existing modules
  type: string;
  displayName: string;
  customDisplayName?: string; // Add custom display name field
  isEnabled: boolean;
  defaultConfig: object;
  deviceTypes: string[];
}

// Intercom call timeout boundaries (in seconds)
const INTERCOM_TIMEOUT_MIN = 10;
const INTERCOM_TIMEOUT_MAX = 180;
const INTERCOM_TIMEOUT_DEFAULT = 60;

function getInitFormData() {
  return {
    apartmentId: '',
    buildingId: '',
    amenityId: '',
    name: '',
    macAddress: 'VI',
    timeZone: '',
    type: IntercomDeviceTypeEnum.Intercom as IntercomDeviceTypeEnum,
    locationVerificationEnabled: false,
    eventPhotoCaptureEnabled: true,
    intercomTimeoutSeconds: INTERCOM_TIMEOUT_DEFAULT,
  };
}

// Room sort by options
const roomSortByOptions = [
  {label: 'Unit Name(Directory Name)', value: RoomSortByEnum.RoomName},
  {label: 'Resident First Name', value: RoomSortByEnum.FirstName},
  {label: 'Resident Last Name', value: RoomSortByEnum.LastName},
  {label: 'Floor Name', value: RoomSortByEnum.FloorName},
];

// Room display format options
const roomDisplayFormatOptions = [
  {label: 'Unit Name(Directory Name)', value: RoomDisplayFormatEnum.RoomName},
  {label: 'Resident Name (First, Last)', value: RoomDisplayFormatEnum.TenantName},
  {label: 'Resident Name (Last, First)', value: RoomDisplayFormatEnum.TenantNameLastFirst},
  {label: 'Unit name and Resident name (First, Last)', value: RoomDisplayFormatEnum.Both},
  {label: 'Unit name and Resident name (Last, First)', value: RoomDisplayFormatEnum.BothLastFirst},
  {label: 'Unit Name With Floor', value: RoomDisplayFormatEnum.RoomNameWithFloor},
  {
    label: 'Resident Name With Floor (First, Last)',
    value: RoomDisplayFormatEnum.TenantNameWithFloor,
  },
  {
    label: 'Resident Name With Floor (Last, First)',
    value: RoomDisplayFormatEnum.TenantNameLastFirstWithFloor,
  },
  {
    label: 'Unit name and Resident name With Floor (First, Last)',
    value: RoomDisplayFormatEnum.BothWithFloor,
  },
  {
    label: 'Unit name and Resident name With Floor (Last, First)',
    value: RoomDisplayFormatEnum.BothLastFirstWithFloor,
  },
];


export default function AddDeviceModal({
  open,
  handleConfirm,
  handleCancel,
  initData,
}: AddDeviceModalProps) {
  const [loading, setLoading] = useState(false);
  const {orgId} = useOrgId();
  const account = useAppSelector(selectAccount);

  // Add state for filtered apartment options
  const [filteredApartmentOptions, setFilteredApartmentOptions] = useState<AutocompleteVal[]>([]);
  const [loadingApartments, setLoadingApartments] = useState(false);

  // Amenity related states
  const [amenityOptions, setAmenityOptions] = useState<AutocompleteVal[]>([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);

  // Combined building/amenity selection
  type LocationOption = {label: string; value: any; group: 'Building' | 'Amenity'};
  const [selectedLocationValue, setSelectedLocationValue] = useState<LocationOption | null>(null);

  // Module related states
  const [deviceCategory, setDeviceCategory] = useState<'VIRTUAL' | 'PHYSICAL'>('VIRTUAL');
  const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
  const [selectedModules, setSelectedModules] = useState<SelectedModule[]>([]);
  const [loadingModules, setLoadingModules] = useState(false);

  // Room configuration states
  const [roomSortBy, setRoomSortBy] = useState<RoomSortByEnum>(RoomSortByEnum.RoomName);
  const [roomDisplayFormat, setRoomDisplayFormat] = useState<RoomDisplayFormatEnum>(
    RoomDisplayFormatEnum.RoomName
  );
  const [loadingRoomConfig, setLoadingRoomConfig] = useState(false);

  const {
    apartmentOptions,
    loadingBuildings,
    buildingOptions,
    selectedApartmentValue,
    buildingSelectDisabled,
    setSelectedApartment,
    setSelectedBuilding,
    handleSelectBuilding: _handleSelectBuilding,
    handleSelectApartment: _handleSelectApartment,
  } = useApartmentBuildingUnit();

  const [formData, setFormData] = useState(getInitFormData());

  // Reset locationVerificationEnabled when manually switching to physical device
  // (not when loading existing device data)
  useEffect(() => {
    if (deviceCategory === 'PHYSICAL' && !initData) {
      setFormData(prev => ({
        ...prev,
        locationVerificationEnabled: false,
      }));
    }
  }, [deviceCategory, initData]);

  // Load available modules when device category changes
  useEffect(() => {
    if (open) {
      setLoadingModules(true);
      getAvailableModules()
        .then(res => {
          if (res.data && res.data.modules) {
            const modulesByCategory =
              deviceCategory === 'VIRTUAL' ? res.data.modules.virtual : res.data.modules.physical;
            setAvailableModules(modulesByCategory);

            // Initialize selected modules with all modules unchecked for new devices
            if (!initData) {
              const initialSelectedModules = modulesByCategory.map(module => ({
                type: module.type,
                displayName: module.displayName,
                customDisplayName: module.displayName, // Initialize with default name
                isEnabled: true, // Changed from false to true
                defaultConfig: module.defaultConfig,
                deviceTypes: module.deviceTypes,
              }));
              setSelectedModules(initialSelectedModules);
            }
          }
        })
        .catch(err => {
          console.error('Error loading available modules:', err);
          message.error('Failed to load available modules');
        })
        .finally(() => {
          setLoadingModules(false);
        });
    }
  }, [open, deviceCategory, initData]);

  // Load existing device modules for edit mode
  useEffect(() => {
    if (initData && initData.id && open) {
      setLoadingModules(true);

      // Load both available modules and current device modules
      Promise.all([getAvailableModules(), getDeviceModules(initData.id)])
        .then(([availableRes, deviceModulesRes]) => {
          if (
            availableRes.data &&
            availableRes.data.modules &&
            deviceModulesRes.data &&
            deviceModulesRes.data.modules
          ) {
            const allAvailableModules = availableRes.data.modules.all;
            const existingModules = deviceModulesRes.data.modules;

            // Filter available modules by device category
            const filteredAvailableModules = allAvailableModules.filter(module =>
              module.deviceTypes.includes(deviceCategory)
            );

            // Create selected modules array with current enabled status and IDs
            const initialSelectedModules = filteredAvailableModules.map(module => {
              const existingModule = existingModules.find(em => em.type === module.type);
              return {
                id: existingModule?.id, // Include existing module ID
                type: module.type,
                displayName: module.displayName,
                customDisplayName: existingModule?.displayName || module.displayName, // Use existing custom name or default
                isEnabled: existingModule ? existingModule.isEnabled : false,
                defaultConfig: module.defaultConfig,
                deviceTypes: module.deviceTypes,
              };
            });

            setAvailableModules(filteredAvailableModules);
            setSelectedModules(initialSelectedModules);
          }
        })
        .catch(err => {
          console.error('Error loading device modules:', err);
          message.error('Failed to load device modules');
        })
        .finally(() => {
          setLoadingModules(false);
        });
    }
  }, [initData, open, deviceCategory]);

  // Load apartments filtered by organization ID and user permissions
  useEffect(() => {
    if (orgId) {
      setLoadingApartments(true);
      const userId = account.userId;
      apartmentServiceFind(orgId, undefined, undefined, userId)
        .then(res => {
          if (res.data && res.data.apartments) {
            const orgApartments = res.data.apartments;
            const options = orgApartments.map((apt: Apartment) => ({
              label: apt.name as string,
              value: apt.id as string,
            }));
            setFilteredApartmentOptions(options);
          }
        })
        .catch(err => {
          console.error('Error loading apartments:', err);
          message.error('Failed to load apartments');
        })
        .finally(() => {
          setLoadingApartments(false);
        });
    }
  }, [orgId, account.userId]);

  // Fetch amenities when apartment selection changes
  useEffect(() => {
    if (formData.apartmentId && orgId) {
      setLoadingAmenities(true);
      amenityServiceFind(orgId, formData.apartmentId)
        .then(res => {
          if (res.data && res.data.amenities) {
            const options = res.data.amenities.map((amenity: Amenity) => ({
              label: amenity.name as string,
              value: amenity.id as string,
            }));
            setAmenityOptions(options);
          } else {
            setAmenityOptions([]);
          }
        })
        .catch(err => {
          console.error('Error loading amenities:', err);
          setAmenityOptions([]);
        })
        .finally(() => {
          setLoadingAmenities(false);
        });
    } else {
      setAmenityOptions([]);
      if (selectedLocationValue?.group === 'Amenity') {
        setSelectedLocationValue(null);
      }
    }
  }, [formData.apartmentId, orgId]);

  // Reset form data when modal opens/closes or initData changes
  useEffect(() => {
    if (open) {
      if (initData) {
        // Edit mode: load existing device data
        console.log('Loading device data:', {
          deviceId: initData.id,
          eventPhotoCaptureEnabled: initData.eventPhotoCaptureEnabled,
        });
        setFormData({
          apartmentId: initData.apartmentId as string,
          buildingId: initData.buildingId as string,
          amenityId: initData.amenityId || '',
          name: initData.name as string,
          macAddress: initData.macAddress || '',
          timeZone: initData.timeZone || '',
          type: initData.type || IntercomDeviceTypeEnum.Intercom,
          locationVerificationEnabled: initData.locationVerificationEnabled || false,
          eventPhotoCaptureEnabled: initData.eventPhotoCaptureEnabled ?? true,
          intercomTimeoutSeconds:
            typeof initData.intercomTimeoutSeconds === 'number'
              ? initData.intercomTimeoutSeconds
              : INTERCOM_TIMEOUT_DEFAULT,
        });

        // Set device category based on existing device type
        if (initData.deviceType) {
          setDeviceCategory(initData.deviceType === 'PHYSICAL' ? 'PHYSICAL' : 'VIRTUAL');
        } else {
          setDeviceCategory('VIRTUAL');
        }

        if (initData.apartment) {
          setSelectedApartment({
            label: initData.apartment.name as string,
            value: initData.apartment.id,
          });
        }
        if (initData.building) {
          setSelectedBuilding({
            label: initData.building.name as string,
            value: initData.building.id,
          });
        }

        // Hydrate combined location selection for edit mode
        if (initData.amenityId && initData.apartment?.amenities) {
          const matchedAmenity = initData.apartment.amenities.find(
            a => a.id === initData.amenityId
          );
          if (matchedAmenity) {
            setSelectedLocationValue({
              label: matchedAmenity.name as string,
              value: matchedAmenity.id as string,
              group: 'Amenity',
            });
          } else {
            setSelectedLocationValue(null);
          }
        } else if (initData.building) {
          setSelectedLocationValue({
            label: initData.building.name as string,
            value: initData.building.id,
            group: 'Building',
          });
        } else {
          setSelectedLocationValue(null);
        }
      } else {
        // Add mode: reset to initial values
        setFormData(getInitFormData());
        setDeviceCategory('VIRTUAL');
        setSelectedApartment(null);
        setSelectedBuilding(null);
        setSelectedLocationValue(null);
        setRoomSortBy(RoomSortByEnum.RoomName);
        setRoomDisplayFormat(RoomDisplayFormatEnum.RoomName);
      }
    }
  }, [open, initData, setSelectedApartment, setSelectedBuilding]);

  // Load room configuration when editing a device
  useEffect(() => {
    if (initData?.id && open) {
      setLoadingRoomConfig(true);
      getRoomConfig(initData.id)
        .then(res => {
          if (res.data && res.data.device) {
            setRoomSortBy(
              (res.data.device.roomSortBy as RoomSortByEnum) || RoomSortByEnum.RoomName
            );
            setRoomDisplayFormat(
              (res.data.device.roomDisplayFormat as RoomDisplayFormatEnum) ||
                RoomDisplayFormatEnum.RoomName
            );
          }
        })
        .catch(err => {
          console.error('Error loading room configuration:', err);
          // Use defaults if loading fails
          setRoomSortBy(RoomSortByEnum.RoomName);
          setRoomDisplayFormat(RoomDisplayFormatEnum.RoomName);
        })
        .finally(() => {
          setLoadingRoomConfig(false);
        });
    }
  }, [initData?.id, open]);

  const handleModuleToggle = (moduleType: string) => {
    setSelectedModules(prev =>
      prev.map(module =>
        module.type === moduleType ? {...module, isEnabled: !module.isEnabled} : module
      )
    );
  };

  const handleModuleNameChange = (moduleType: string, newName: string) => {
    setSelectedModules(prev =>
      prev.map(module =>
        module.type === moduleType ? {...module, customDisplayName: newName} : module
      )
    );
  };

  const handleAddDevice = async () => {
    // Validate MAC address
    const cleanMacAddress = formData.macAddress.replace(/[^0-9A-Fa-fVI]/g, '');

    if (!validateMacAddress(cleanMacAddress)) {
      message.error(
        'Invalid MAC address format. Please enter 12 hexadecimal characters without separators.'
      );
      return;
    }

    // Validate intercom call timeout (20-180s)
    if (
      !Number.isFinite(formData.intercomTimeoutSeconds) ||
      formData.intercomTimeoutSeconds < INTERCOM_TIMEOUT_MIN ||
      formData.intercomTimeoutSeconds > INTERCOM_TIMEOUT_MAX
    ) {
      message.error(
        `Call timeout must be between ${INTERCOM_TIMEOUT_MIN} and ${INTERCOM_TIMEOUT_MAX} seconds.`
      );
      return;
    }

    setLoading(true);

    try {
      // Create device first
      const deviceResponse = await intercomDeviceServiceCreate({
        intercomDevice: {
          ...formData,
          macAddress: cleanMacAddress,
          orgId,
        },
      });

      // If device creation is successful and there are selected modules, create modules
      if (deviceResponse.data && deviceResponse.data.id) {
        const enabledModules = selectedModules.filter(module => module.isEnabled);

        if (enabledModules.length > 0) {
          const modulesToCreate: DeviceModule[] = enabledModules.map(module => ({
            type: module.type as DeviceModuleTypeEnum,
            displayName: module.customDisplayName || module.displayName,
            displayOrder: 1,
            isEnabled: true,
            deviceTypes: [deviceCategory as DeviceModuleDeviceTypeEnum],
          }));

          await createDeviceModules(deviceResponse.data.id, {
            modules: modulesToCreate,
          });
        }

        // Save room configuration
        await updateRoomConfig(deviceResponse.data.id, {
          roomSortBy,
          roomDisplayFormat,
        });
      }

      message.success('Device added successfully.');
      handleConfirm();
    } catch (err: any) {
      const errorMessage = err.message || 'An unexpected error occurred.';
      message.error(`Failed to add device: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDevice = async () => {
    // Validate MAC address
    const cleanMacAddress = formData.macAddress.replace(/[^0-9A-Fa-fVI]/g, '');

    if (!validateMacAddress(cleanMacAddress)) {
      message.error(
        'Invalid MAC address format. Please enter 12 hexadecimal characters without separators.'
      );
      return;
    }

    // Validate intercom call timeout (20-180s)
    if (
      !Number.isFinite(formData.intercomTimeoutSeconds) ||
      formData.intercomTimeoutSeconds < INTERCOM_TIMEOUT_MIN ||
      formData.intercomTimeoutSeconds > INTERCOM_TIMEOUT_MAX
    ) {
      message.error(
        `Call timeout must be between ${INTERCOM_TIMEOUT_MIN} and ${INTERCOM_TIMEOUT_MAX} seconds.`
      );
      return;
    }

    setLoading(true);

    try {
      // Update device first
      console.log('Updating device with formData:', {
        deviceId: initData?.id,
        eventPhotoCaptureEnabled: formData.eventPhotoCaptureEnabled,
      });
      await intercomDeviceServiceUpdate(initData?.id as string, {
        intercomDevice: {
          ...formData,
          macAddress: cleanMacAddress,
          orgId,
        },
      });

      // Use createDeviceModules for updating modules
      // This will handle both enabling new modules and updating existing ones
      const allModules = selectedModules.map((module, index) => ({
        id: module.id, // Include the existing module ID (undefined for new modules)
        type: module.type as DeviceModuleTypeEnum,
        displayName: module.customDisplayName || module.displayName,
        displayOrder: index + 1,
        isEnabled: module.isEnabled, // Send the actual enabled status
        deviceTypes: [deviceCategory as DeviceModuleDeviceTypeEnum],
      }));

      await createDeviceModules(initData?.id as string, {
        modules: allModules,
      });

      // Update room configuration
      await updateRoomConfig(initData?.id as string, {
        roomSortBy,
        roomDisplayFormat,
      });

      message.success('Device updated successfully.');
      handleConfirm();
    } catch (err: any) {
      message.error(`Failed to update device: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectApartment = (e: any, val: AutocompleteVal) => {
    _handleSelectApartment(e, val);
    setFormData({...formData, apartmentId: val?.value, amenityId: '', buildingId: ''});
    setSelectedLocationValue(null);
    setSelectedBuilding(null);
  };

  const handleSelectLocation = (_e: any, val: LocationOption | null) => {
    setSelectedLocationValue(val);
    if (!val) {
      setFormData({...formData, buildingId: '', amenityId: ''});
      setSelectedBuilding(null);
    } else if (val.group === 'Building') {
      _handleSelectBuilding(_e, val);
      setFormData({...formData, buildingId: val.value, amenityId: ''});
    } else {
      setSelectedBuilding(null);
      setFormData({...formData, amenityId: val.value, buildingId: ''});
    }
  };

  const combinedLocationOptions: LocationOption[] = useMemo(() => {
    const bldgOpts: LocationOption[] = buildingOptions
      .filter((b): b is AutocompleteVal & {label: string} => !!b?.label)
      .map(b => ({label: b.label, value: b.value, group: 'Building' as const}));
    const amenOpts: LocationOption[] = amenityOptions
      .filter((a): a is AutocompleteVal & {label: string} => !!a?.label)
      .map(a => ({label: a.label, value: a.value, group: 'Amenity' as const}));
    return [...bldgOpts, ...amenOpts];
  }, [buildingOptions, amenityOptions]);

  const isEdit = useMemo(() => {
    return !!initData;
  }, [initData]);

  const validateMacAddress = (mac: string) => {
    const macRegex = /^[0-9A-Fa-fVI]{12}$/;
    return macRegex.test(mac);
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Device' : 'Add Device'}
      handleCancel={handleCancel}
      handleConfirm={isEdit ? handleEditDevice : handleAddDevice}
      loading={loading}
      modalContent={
        <Box component="form">
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Device Name
          </Typography>
          <TextField
            fullWidth
            required
            id="deviceName"
            placeholder="Device Name"
            variant="outlined"
            size="small"
            value={formData.name}
            onChange={(e: any) => {
              setFormData({...formData, name: e.target.value});
            }}
            sx={{mb: 3}}
          />

          <MacAddressInput
            value={formData.macAddress}
            onChange={newMac => setFormData({...formData, macAddress: newMac})}
            fixedPrefix="VI"
          />

          {/* Logo Upload - Only show in edit mode */}
          {isEdit && initData?.id && (
            <>
              <Typography variant="h6" sx={{mb: 1, mt: 3, fontWeight: 400, fontSize: '1.1rem'}}>
                Device Logo
              </Typography>
              <Box sx={{mb: 3}}>
                <LogoUpload deviceId={initData.id} />
              </Box>
            </>
          )}

          {/* Device Category Selection */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Device Category
          </Typography>
          <FormControl component="fieldset" sx={{mb: 3}}>
            <RadioGroup
              row
              value={deviceCategory}
              onChange={e => setDeviceCategory(e.target.value as 'VIRTUAL' | 'PHYSICAL')}
            >
              <FormControlLabel value="VIRTUAL" control={<Radio />} label="Virtual Device" />
              <FormControlLabel value="PHYSICAL" control={<Radio />} label="Physical Device" />
            </RadioGroup>
          </FormControl>

          {/* Available Modules - Only show for Virtual devices */}
          {deviceCategory === 'VIRTUAL' && (
            <>
              <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
                Available Modules
              </Typography>
              <Paper variant="outlined" sx={{p: 2, mb: 3, maxHeight: 300, overflow: 'auto'}}>
                {loadingModules ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
                    <CircularProgress size={24} />
                    <Typography sx={{ml: 2}}>Loading modules...</Typography>
                  </Box>
                ) : (
                  <FormGroup>
                    {selectedModules.map(module => (
                      <Box key={module.type} sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={module.isEnabled}
                              onChange={() => handleModuleToggle(module.type)}
                            />
                          }
                          label=""
                          sx={{mr: 1}}
                        />
                        <Box sx={{flexGrow: 1}}>
                          <TextField
                            size="small"
                            fullWidth
                            value={module.customDisplayName || module.displayName}
                            onChange={e => handleModuleNameChange(module.type, e.target.value)}
                            placeholder={`Enter name for ${module.type}`}
                            variant="outlined"
                            sx={{mb: 0.5}}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {module.type}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {selectedModules.length === 0 && !loadingModules && (
                      <Typography color="text.secondary">
                        No modules available for this device type
                      </Typography>
                    )}
                  </FormGroup>
                )}
              </Paper>
            </>
          )}

          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Search property
          </Typography>
          <Autocomplete
            size="small"
            options={filteredApartmentOptions}
            fullWidth
            loading={loadingApartments}
            value={selectedApartmentValue}
            onChange={handleSelectApartment}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search property"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingApartments ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{mb: 3}}
          />

          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Select Building / Amenity
          </Typography>
          <Autocomplete
            size="small"
            disabled={buildingSelectDisabled && amenityOptions.length === 0 && !loadingAmenities}
            fullWidth
            options={combinedLocationOptions}
            groupBy={option => option.group}
            value={selectedLocationValue}
            onChange={handleSelectLocation}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value && option.group === value.group
            }
            renderGroup={params => (
              <li key={params.key}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    px: 2,
                    pt: 1.5,
                    pb: 0.5,
                  }}
                >
                  {params.group === 'Building' ? '🏢 Buildings' : '🏊 Amenities'}
                </Typography>
                <ul style={{padding: 0}}>{params.children}</ul>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  !formData.apartmentId
                    ? 'Select a property first'
                    : 'Select Building or Amenity'
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingBuildings || loadingAmenities ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Time Zone Selector */}
          <Typography variant="h6" sx={{mb: 1, mt: 2, fontWeight: 400, fontSize: '1.1rem'}}>
            Time Zone
          </Typography>
          <FormControl fullWidth sx={{mb: 3}}>
            <InputLabel id="timezone-label">Time Zone</InputLabel>
            <Select
              labelId="timezone-label"
              id="timezone"
              value={formData.timeZone}
              label="Time Zone"
              onChange={e => setFormData({...formData, timeZone: e.target.value})}
            >
              {TIMEZONE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Room Configuration - Only show in edit mode */}
          {isEdit && initData?.id && (
            <>
              <Typography variant="h6" sx={{mb: 1, mt: 2, fontWeight: 400, fontSize: '1.1rem'}}>
                Directory Display Configuration
              </Typography>
              <FormControl fullWidth sx={{mb: 2}}>
                <InputLabel id="room-sort-by-label">Directory Sort By</InputLabel>
                <Select
                  labelId="room-sort-by-label"
                  id="room-sort-by"
                  value={roomSortBy}
                  label="Directory Sort By"
                  onChange={e => setRoomSortBy(e.target.value as RoomSortByEnum)}
                  disabled={loadingRoomConfig}
                >
                  {roomSortByOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{mb: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                  <Typography variant="h6" sx={{fontWeight: 400, fontSize: '1.1rem'}}>
                    Directory Display Format
                  </Typography>
                  <Tooltip
                    title={
                      <Box component="div" sx={{fontSize: '0.875rem'}}>
                        <Box sx={{fontWeight: 600, mb: 0.5}}>Display Format Examples:</Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Unit Name:</strong> Unit 101, Unit 102
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Resident Name (First, Last):</strong> John Doe, Jane Smith, Room
                          103 (if no resident)
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Resident Name (Last, First):</strong> Doe, John; Smith, Jane; Room
                          103 (if no resident)
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Unit name and Resident name (First, Last):</strong> Unit 101 (John
                          Doe), Room 102 (Jane Smith)
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Unit name and Resident name (Last, First):</strong> Unit 101 (Doe,
                          John), Room 102 (Smith, Jane)
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Unit Name With Floor:</strong> Floor 1 - Unit 101, Floor 2 - Unit
                          201
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Resident Name With Floor (First, Last):</strong> Floor 1 - John
                          Doe, Floor 2 - Bob Johnson
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Resident Name With Floor (Last, First):</strong> Floor 1 - Doe,
                          John; Floor 2 - Johnson, Bob
                        </Box>
                        <Box sx={{mb: 0.5}}>
                          <strong>Unit name and Resident name With Floor (First, Last):</strong>{' '}
                          Floor 1 - Unit 101 (John Doe), Floor 2 - Unit 201 (Bob Johnson)
                        </Box>
                        <Box>
                          <strong>Unit name and Resident name With Floor (Last, First):</strong>{' '}
                          Floor 1 - Unit 101 (Doe, John), Floor 2 - Unit 201 (Johnson, Bob)
                        </Box>
                      </Box>
                    }
                    arrow
                    placement="right"
                    sx={{
                      tooltip: {
                        maxWidth: 700,
                      },
                    }}
                  >
                    <IconButton size="small" sx={{ml: 1}}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormControl fullWidth>
                  <InputLabel id="room-display-format-label">Directory Display Format</InputLabel>
                  <Select
                    labelId="room-display-format-label"
                    id="room-display-format"
                    value={roomDisplayFormat}
                    label="Directory Display Format"
                    onChange={e => setRoomDisplayFormat(e.target.value as RoomDisplayFormatEnum)}
                    disabled={loadingRoomConfig}
                  >
                    {roomDisplayFormatOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {/* Location Verification Checkbox - Only show for Virtual devices */}
          {deviceCategory === 'VIRTUAL' && (
            <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.locationVerificationEnabled}
                    onChange={e =>
                      setFormData({...formData, locationVerificationEnabled: e.target.checked})
                    }
                  />
                }
                label="Enable Location Verification"
              />
              <Tooltip
                title="When enabled, your phone must be within 100 ft of the property to scan the QR code. The code is linked to the property's address."
                arrow
                placement="right"
              >
                <IconButton size="small" sx={{ml: 1}}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Event Photo Capture Checkbox */}
          <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.eventPhotoCaptureEnabled}
                  onChange={e =>
                    setFormData({...formData, eventPhotoCaptureEnabled: e.target.checked})
                  }
                />
              }
              label="Enable Event Photo Capture"
            />
            <Tooltip
              title="When enabled, the device will capture photos during events for security and monitoring purposes."
              arrow
              placement="right"
            >
              <IconButton size="small" sx={{ml: 1}}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Call Timeout (seconds) - applies to virtual & physical intercom devices */}
          <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
            <TextField
              type="number"
              label="Call Timeout (seconds)"
              value={formData.intercomTimeoutSeconds}
              onChange={e => {
                const raw = e.target.value;
                // Allow the user to clear the field while typing; we'll re-validate on submit
                const parsed = raw === '' ? NaN : Number(raw);
                setFormData({
                  ...formData,
                  intercomTimeoutSeconds: parsed as number,
                });
              }}
              onBlur={() => {
                // Clamp on blur so the field always shows a valid value
                const current = Number(formData.intercomTimeoutSeconds);
                let next = Number.isFinite(current) ? current : INTERCOM_TIMEOUT_DEFAULT;
                if (next < INTERCOM_TIMEOUT_MIN) next = INTERCOM_TIMEOUT_MIN;
                if (next > INTERCOM_TIMEOUT_MAX) next = INTERCOM_TIMEOUT_MAX;
                if (next !== formData.intercomTimeoutSeconds) {
                  setFormData({...formData, intercomTimeoutSeconds: next});
                }
              }}
              inputProps={{
                min: INTERCOM_TIMEOUT_MIN,
                max: INTERCOM_TIMEOUT_MAX,
                step: 1,
              }}
              helperText={`Allowed range: ${INTERCOM_TIMEOUT_MIN}–${INTERCOM_TIMEOUT_MAX} seconds`}
              sx={{flex: 1}}
            />
            <Tooltip
              title="When the device places a call to a resident or reception, it will automatically hang up if no one joins within this many seconds and notify the caller that no one is answering."
              arrow
              placement="right"
            >
              <IconButton size="small" sx={{ml: 1}}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      }
    />
  );
}
